import { logger } from '../../services/logger';
import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/db';
import { Market } from '../../types';
import { toast } from 'react-hot-toast';
import { AlertCircle, RefreshCw, Loader2 } from 'lucide-react';
import { useAuth } from '../../lib/auth';

interface MarketCancellerProps {
  market: Market;
  marketId: string;
  onCancelled: () => void;
  onRefundsProcessed?: () => void;
}

export const MarketCanceller: React.FC<MarketCancellerProps> = ({ market: initialMarket, marketId, onCancelled, onRefundsProcessed }) => {
  const [market, setMarket] = useState<Market>(initialMarket);
  const [reason, setReason] = useState('');
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  const [isRefunding, setIsRefunding] = useState(false);
  const [refundError, setRefundError] = useState<string | null>(null);
  const [refundMessage, setRefundMessage] = useState<string | null>(null);

  const [showCancelConfirmModal, setShowCancelConfirmModal] = useState(false);
  const [showRefundConfirmModal, setShowRefundConfirmModal] = useState(false);

  const { user } = useAuth();
  const isCreator = user?.id === market.created_by;

  useEffect(() => {
    setMarket(initialMarket);
  }, [initialMarket]);

  const triggerCancelMarket = () => {
    if (!isCreator) {
      toast.error("Only the market creator can cancel this market.");
      return;
    }
    if (!reason.trim()) {
      setCancelError('A reason for cancellation is required.');
      toast.error('A reason for cancellation is required.');
      return;
    }
    if (market.status === 'cancelled') {
      toast.error('This market has already been cancelled.');
      return;
    }
    if (!['active', 'pending_approval', 'ended', 'expired'].includes(market.status)) {
      toast.error(`Market status "${market.status}" does not allow cancellation by creator.`);
      return;
    }
    setShowCancelConfirmModal(true);
  };

  const executeCancellation = async () => {
    setShowCancelConfirmModal(false);
    setIsCancelling(true);
    setCancelError(null);

    try {
      const { error: rpcError } = await supabase.rpc('cancel_private_market', {
        p_market_id: marketId,
        p_reason: reason.trim(),
      });

      if (rpcError) {
        throw rpcError;
      }

      toast.success('Market cancelled successfully!');
      setReason('');
      setMarket(prevMarket => ({ ...prevMarket, status: 'cancelled', cancellation_reason: reason.trim() }));
      if (onCancelled) {
        onCancelled();
      }
    } catch (err: unknown) {
      logger.error('Error cancelling market:', err);
      const errorMessage = (err as { message?: string })?.message || 'Failed to cancel market. Please try again.';
      setCancelError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsCancelling(false);
    }
  };

  const triggerProcessRefunds = () => {
    if (!isCreator) {
      toast.error("Only the market creator can process refunds for this market.");
      return;
    }
    if (market.status !== 'cancelled') {
      toast.error("Refunds can only be processed for cancelled markets.");
      return;
    }
    setShowRefundConfirmModal(true);
  };

  const executeRefunds = async () => {
    setShowRefundConfirmModal(false);
    setIsRefunding(true);
    setRefundError(null);
    setRefundMessage(null);

    try {
      const { data, error: rpcError } = await supabase.rpc('process_private_market_creator_refunds', {
        p_market_id: marketId,
      });

      if (rpcError) {
        throw rpcError;
      }

      const count = (data as { count?: number })?.count;
      const message = (data as { message?: string })?.message;

      if (typeof count === 'number') {
        toast.success(`${count} trades refunded successfully.`);
        setRefundMessage(`${count} trades refunded successfully.`);
      } else if (message) {
        toast.success(message);
        setRefundMessage(message);
      } else {
        toast.success('Refund process completed.');
        setRefundMessage('Refund process completed.');
      }

      if (onRefundsProcessed) {
        onRefundsProcessed();
      }
    } catch (err: unknown) {
      logger.error('Error processing refunds:', err);
      const errorMessage = (err as { message?: string })?.message || 'Failed to process refunds. Please try again.';
      setRefundError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsRefunding(false);
    }
  };

  if (market.status === 'cancelled' && isCreator) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Market Cancelled: "{market.title}"</h3>
        <div className="mb-4 p-3 bg-amber-50 border border-amber-300 rounded-md text-sm text-amber-700">
          <p className="font-medium">This market has been cancelled.</p>
          {market.cancellation_reason && (
            <p className="mt-1">Reason: {market.cancellation_reason}</p>
          )}
        </div>

        <div className="mt-6 border-t pt-6">
          <h4 className="text-lg font-semibold text-gray-700 mb-2">Process Refunds</h4>
          <p className="text-sm text-gray-600 mb-4">
            As the creator of this cancelled private market, you can initiate the refund process for all active trades.
            This will attempt to return the trade amounts to the respective users' wallets.
          </p>
          <button
            onClick={triggerProcessRefunds}
            disabled={isRefunding}
            className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-70 disabled:cursor-not-allowed transition-all flex items-center justify-center"
          >
            {isRefunding && !showRefundConfirmModal ? (
              <>
                <RefreshCw className="animate-spin w-5 h-5 mr-2" />
                Processing Refunds...
              </>
            ) : (
              'Process All Refunds'
            )}
          </button>
          {refundError && <p className="text-xs text-red-600 mt-2 text-center">Error: {refundError}</p>}
          {refundMessage && <p className="text-xs text-green-600 mt-2 text-center">{refundMessage}</p>}
        </div>

        {showRefundConfirmModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75 overflow-y-auto h-full w-full z-50 flex justify-center items-center p-4">
            <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md mx-auto">
              <h4 className="text-lg font-medium mb-4 text-gray-800">Confirm Refund Processing</h4>
              <div className="bg-blue-50 p-3 rounded-lg mb-4">
                <p className="text-blue-700">
                  Are you sure you want to process refunds for all trades in this market? This will attempt to return funds to traders.
                </p>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowRefundConfirmModal(false)}
                  disabled={isRefunding}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={executeRefunds}
                  disabled={isRefunding}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center"
                >
                  {isRefunding ? (
                    <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Processing...</>
                  ) : (
                    'Yes, Process Refunds'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (market.status === 'cancelled' && !isCreator) {
    return (
      <div className="p-4 bg-yellow-50 border border-yellow-300 rounded-md text-yellow-700">
        <p className="font-medium">This market has been cancelled.</p>
        {market.cancellation_reason && (
          <p className="text-sm mt-1">Reason: {market.cancellation_reason}</p>
        )}
        <p className="text-xs mt-2">Refund processing is handled by the market creator.</p>
      </div>
    );
  }

  if (!isCreator || !['active', 'pending_approval', 'ended', 'expired'].includes(market.status)) {
    return (
      <div className="p-4 bg-gray-100 border border-gray-300 rounded-md text-gray-700">
        <p className="font-medium">
          { !isCreator ? "Market cancellation is only available to the creator." :
            `This market (status: ${market.status}) cannot be cancelled by the creator at this stage.`
          }
        </p>
        {!isCreator && <p className="text-sm mt-1">Please contact the admin if there is an issue.</p>}
        {isCreator && !['active', 'pending_approval', 'ended', 'expired'].includes(market.status) && 
         <p className="text-sm mt-1">Only markets that are active, pending approval, ended, or expired can be cancelled by the creator.</p>}
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
      <h3 className="text-xl font-semibold text-gray-800 mb-4">Cancel Market: "{market.title}"</h3>
      <div className="mb-6 p-4 bg-red-50 border-red-200 rounded-md text-sm text-red-700">
        <div className="flex items-start">
          <AlertCircle className="w-5 h-5 mr-3 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium">Important Considerations:</p>
            <ul className="list-disc list-inside ml-1 space-y-1 mt-1">
              <li>Cancelling this market is irreversible.</li>
              <li>The market status will be set to "cancelled".</li>
              <li>Ensure you have a process for any necessary bettor notifications or refunds as per your platform's rules (this action does not automate refunds).</li>
              <li>A clear reason for cancellation is mandatory.</li>
            </ul>
          </div>
        </div>
      </div>
      <div className="space-y-4">
        <div>
          <label htmlFor="cancellationReason" className="block text-sm font-medium text-gray-700 mb-1">
            Reason for Cancellation <span className="text-red-500">*</span>
          </label>
          <textarea
            id="cancellationReason"
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              if (cancelError && e.target.value.trim()) setCancelError(null);
            }}
            placeholder="Clearly explain why this market is being cancelled (e.g., critical error in market setup, unforeseen external event making it void, etc.)"
            className={`w-full px-3 py-2 text-gray-700 border rounded-lg focus:outline-none focus:ring-2 transition-colors ${
              cancelError && !reason.trim() ? 'border-red-500 focus:ring-red-500/50 focus:border-red-500' : 'border-gray-300 focus:ring-wayja-teal/50 focus:border-wayja-teal'
            }`}
            rows={4}
            disabled={isCancelling}
            aria-describedby={cancelError && !reason.trim() ? "reason-error" : undefined}
          />
          {cancelError && !reason.trim() && <p id="reason-error" className="text-xs text-red-600 mt-1">{cancelError}</p>}
        </div>
        {cancelError && reason.trim() && (
          <div className="p-3 bg-red-50 border border-red-300 rounded-md text-sm text-red-700" role="alert">
            {cancelError}
          </div>
        )}
        <div className="flex justify-end pt-2">
          <button
            onClick={triggerCancelMarket}
            disabled={isCancelling}
            className="px-6 py-2.5 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed transition-all"
          >
            {isCancelling && !showCancelConfirmModal ? (
              <>
                <RefreshCw className="animate-spin w-5 h-5 mr-2" />
                Cancelling...
              </>
            ) : (
              'Confirm and Cancel Market'
            )}
          </button>
        </div>
      </div>

      {showCancelConfirmModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 overflow-y-auto h-full w-full z-50 flex justify-center items-center p-4">
          <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md mx-auto">
            <h4 className="text-lg font-medium mb-4 text-gray-800">Confirm Market Cancellation</h4>
            <div className="bg-red-50 p-3 rounded-lg mb-4">
              <p className="text-red-700">
                Are you sure you want to cancel this market? This action cannot be undone. The market will become inactive, and you may need to coordinate refunds if trades were placed.
              </p>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowCancelConfirmModal(false)}
                disabled={isCancelling}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50"
              >
                Back
              </button>
              <button
                type="button"
                onClick={executeCancellation}
                disabled={isCancelling}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 flex items-center"
              >
                {isCancelling ? (
                  <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Confirming...</>
                ) : (
                  'Yes, Cancel Market'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}; 